import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/**
 * The global website/mobile template catalog — mirrors stacks-agents
 * `src/app/api/templates/`. Reads are open to any builder user; every write is
 * admin-only, enforced server-side.
 */
@Injectable({ providedIn: 'root' })
export class TemplatesApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API*/
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getWebsiteTemplates(): Promise<Record<string, any>> {
    return this.http
      .get<{ data?: Record<string, any> }>(`${environment.agentsUrl}v4/retrieval/templates/website`, {
        headers: this.agentsAuthHeaders(),
      })
      .pipe(
        map(body => body?.data ?? {}),
        catchError(() => of({})),
      )
      .toPromise() as Promise<Record<string, any>>
  }

  getWebsiteTemplate(id: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/templates/website/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { id },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  getMobileTemplates(): Promise<Record<string, any>> {
    return this.http
      .get<{ data?: Record<string, any> }>(`${environment.agentsUrl}v4/retrieval/templates/mobile`, {
        headers: this.agentsAuthHeaders(),
      })
      .pipe(
        map(body => body?.data ?? {}),
        catchError(() => of({})),
      )
      .toPromise() as Promise<Record<string, any>>
  }

  getMobileTemplate(id: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/templates/mobile/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { id },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  /** Creates a website template (admin only). @returns { key, response } or null on failure. */
  async insertWebsiteTemplate(templateData: Record<string, any>): Promise<{ key: string; response: any } | null> {
    const res = await this.http
      .post<{ data: { key: string; response: any } }>(`${environment.agentsUrl}v4/creation/templates/website`,
        { templateData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data ?? null
  }

  /** Creates a mobile app template (admin only). @returns { key, response } or null on failure. */
  async insertMobileTemplate(templateData: Record<string, any>): Promise<{ key: string; response: any } | null> {
    const res = await this.http
      .post<{ data: { key: string; response: any } }>(`${environment.agentsUrl}v4/creation/templates/mobile`,
        { templateData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data ?? null
  }

  /** Updates a website template (admin only). */
  async updateWebsiteTemplate(id: string, templateData: Record<string, any>): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/templates/website`,
        { id, templateData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Updates a mobile app template (admin only). */
  async updateMobileTemplate(id: string, templateData: Record<string, any>): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/templates/mobile`,
        { id, templateData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a website template (admin only). */
  async deleteWebsiteTemplate(id: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/templates/website`, {
        headers: this.agentsAuthHeaders(),
        body: { id },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a mobile app template (admin only). */
  async deleteMobileTemplate(id: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/templates/mobile`, {
        headers: this.agentsAuthHeaders(),
        body: { id },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
